// "Add SOA — Statement of Account" modal (mockup 2). Supplier + month pickers
// drive which per-restaurant breakdown loads (via the same supplier-details
// endpoint the expanded row uses), so switching either just re-fetches the
// restaurant list and re-derives the pre-filled Debit/Credit inputs. Real SOAs
// are per-restaurant statements: Debit = orders submitted (the displayed SOA
// GMV column), Credit = orders paid — optional, and when entered it becomes
// the effective SOA figure the BE uses for variance/discrepancy/invoice base
// (`effectiveSoa = soaCredit ?? soaGmv`, "Paid wins when entered"); revenue
// stays Kaso-GMV-based either way. Multiple statement files can be
// attached, each tagged to a restaurant (or left supplier-wide); files +
// entries go out together as one multipart PUT via `useSaveSoaApi`. Already
// uploaded documents for this supplier/month are listed below with a delete
// action — documents accumulate across saves, they are never replaced by a
// re-save of the entries.
import type { UploadedFile } from '@elkaso-app/web-design';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  ChakraAsyncSelect,
  ChakraSelect,
  FileUploader,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButtonWithTooltip,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Stack,
  Text,
  useCustomToast,
} from '@elkaso-app/web-design';
import {
  ReconciliationSupplierOption,
  SoaEntryInput,
  useDeleteReconciliationDocumentApi,
  useGetReconciliationSupplierDetailsApi,
  useSaveSoaApi,
  useSearchReconciliationSuppliersApi,
} from 'apis/reconciliation';
import dayjs from 'dayjs';
import { formatMonthLabel, shiftMonth } from 'pages/reconciliation/utils/month';
import { STATEMENT_ACCEPT_TYPES } from 'pages/reconciliation/utils/statement-files';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FiTrash2 } from 'react-icons/fi';
import { useQueryClient } from 'react-query';
import { Link as RouterLink } from 'react-router-dom';

const MAX_SOA_FILES = 10;

type TMonthOption = { value: string; label: string };
type TFileRestaurantOption = { value: number | null; label: string };

interface FormValues {
  supplier: ReconciliationSupplierOption | null;
  month: TMonthOption;
}

interface EntryValue {
  soaGmv: string;
  soaCredit: string;
}

interface FileEntry {
  file: UploadedFile;
  restaurantId: number | null;
}

export interface IAddSoaModal {
  isOpen: boolean;
  onClose: () => void;
  /** Page month — the default month-select value and the origin for the "last 12 months" list. */
  month: string;
  /** When set (the expanded row's "Edit SOA" entry point), the supplier select opens pre-selected
   * with this option instead of empty — it stays enabled, this is only a starting value. */
  initialSupplier?: ReconciliationSupplierOption | null;
}

const buildMonthOptions = (month: string): TMonthOption[] => {
  const options: TMonthOption[] = [];
  let cursor = month;
  for (let i = 0; i < 12; i++) {
    options.push({ value: cursor, label: formatMonthLabel(cursor) });
    cursor = shiftMonth(cursor, -1);
  }
  return options;
};

// Identifies a File by content rather than reference — `onUpload` may hand back either the
// full current selection or just the newly dropped files depending on the uploader's internal
// bookkeeping, and this lets the merge in handleFileUpload be correct either way.
const fileFingerprint = (file: File) => `${file.name}::${file.size}::${file.lastModified}`;

export const AddSoaModal = ({ isOpen, onClose, month, initialSupplier }: IAddSoaModal) => {
  const toast = useCustomToast();
  const queryClient = useQueryClient();

  const monthOptions = useMemo(() => buildMonthOptions(month), [month]);

  const defaultValues: FormValues = { supplier: null, month: monthOptions[0] };

  const { control, watch, reset } = useForm<FormValues>({ defaultValues });
  const selectedSupplier = watch('supplier');
  const selectedMonth = watch('month');

  const [entryValues, setEntryValues] = useState<Record<number, EntryValue>>({});
  const [entryError, setEntryError] = useState<string | null>(null);
  const [fileEntries, setFileEntries] = useState<FileEntry[]>([]);
  const [deletingDocumentId, setDeletingDocumentId] = useState<number | null>(null);

  // The modal stays mounted while the screen lives, so useForm's defaultValues
  // captured the page month at first render only — re-derive them at each open
  // so the month select follows the MonthSwitcher, and honor a per-open
  // `initialSupplier` preselection (the expanded row's "Edit SOA" entry point)
  // instead of always resetting the supplier to empty.
  useEffect(() => {
    if (!isOpen) return;
    reset({ supplier: initialSupplier ?? null, month: monthOptions[0] });
    setEntryValues({});
    setEntryError(null);
    setFileEntries([]);
  }, [isOpen]);

  // A stale validation error/file selection shouldn't outlive the selection it was raised for.
  useEffect(() => {
    setEntryError(null);
    setFileEntries([]);
  }, [selectedSupplier, selectedMonth]);

  // Nulling the supplierId while the modal is closed (mirrors `supplier-row.tsx`'s
  // isExpanded-gated fetch) keeps this query dormant until it's actually needed.
  const activeSupplierId = isOpen ? selectedSupplier?.value ?? null : null;
  const activeMonth = selectedMonth?.value ?? month;
  // Same target as the expanded row's "missing commission" link (supplier-row.tsx) — the GPO
  // Orders list filtered to this supplier's orders for the active month.
  const pendingTermsMonthStart = `${activeMonth}-01`;
  const pendingTermsMonthEnd = dayjs(pendingTermsMonthStart).endOf('month').format('YYYY-MM-DD');

  // Async search over supplier profiles linked to a supplier (only those are
  // reconcilable) — same pattern as the inquiries flow's supplier picker
  // (useSearchQuotationSuppliersApi). Not scoped to the selected month: the
  // supplier list itself doesn't vary by month, only its computed figures do.
  const { loadOptions: loadSupplierOptions } = useSearchReconciliationSuppliersApi();

  const {
    data: detail,
    isLoading: isDetailLoading,
    isSuccess: isDetailSuccess,
  } = useGetReconciliationSupplierDetailsApi(activeMonth, activeSupplierId);

  const restaurants = detail?.restaurants ?? [];
  const isClosed = detail?.status === 'closed';
  const documents = detail?.documents ?? [];
  // BE now returns a normal (non-404) detail payload with restaurants: [] for a valid GPO
  // supplier that simply had no Kaso orders that month — 404 is reserved for "not a GPO
  // supplier". Gate on isDetailSuccess so this never fires while loading or after an error.
  const hasNoOrders = isDetailSuccess && restaurants.length === 0;
  const pendingTermsCount = detail?.commissionBreakdown?.pendingTerms ?? 0;

  const fileRestaurantOptions = useMemo<TFileRestaurantOption[]>(
    () => [
      { value: null, label: 'Entire supplier' },
      ...restaurants.map((r) => ({ value: r.restaurantId, label: r.restaurantName })),
    ],
    [restaurants]
  );

  // Re-derive the pre-filled inputs whenever the loaded detail changes (new
  // supplier/month picked, or a fresh fetch resolves).
  useEffect(() => {
    if (!detail) {
      setEntryValues({});
      return;
    }
    const next: Record<number, EntryValue> = {};
    detail.restaurants.forEach((r) => {
      next[r.restaurantId] = {
        soaGmv: r.soaGmv !== null ? String(r.soaGmv) : '',
        soaCredit: r.soaCredit !== null ? String(r.soaCredit) : '',
      };
    });
    setEntryValues(next);
  }, [detail]);

  // No state teardown needed here — the open-effect above rebuilds everything
  // from the current props on the next open.
  const handleClose = () => onClose();

  // Same 3 query families as the collection/close/reopen mutations elsewhere in this feature —
  // the detail fetch backing this modal, the suppliers list rows, and the month's KPI summary.
  const invalidate = () => {
    queryClient.invalidateQueries(['reconciliation-supplier-details', activeMonth, activeSupplierId]);
    queryClient.invalidateQueries(['reconciliation-suppliers']);
    queryClient.invalidateQueries(['reconciliation-summary', activeMonth]);
  };

  const { mutate: saveSoa, isLoading: isSaving } = useSaveSoaApi({
    onSuccess: (_data, variables) => {
      toast({ description: 'SOA saved', status: 'success' });
      queryClient.invalidateQueries(['reconciliation-supplier-details', variables.month, variables.supplierId]);
      queryClient.invalidateQueries(['reconciliation-suppliers']);
      queryClient.invalidateQueries(['reconciliation-summary', variables.month]);
      handleClose();
    },
  });

  const { mutate: deleteDocument, isLoading: isDeletingDocument } = useDeleteReconciliationDocumentApi({
    onSuccess: () => {
      toast({ description: 'Statement document deleted', status: 'success' });
      invalidate();
    },
    onSettled: () => setDeletingDocumentId(null),
  });

  const handleFileUpload = (uploaded: UploadedFile[]) => {
    setFileEntries((prev) => {
      const existingKeys = new Set(prev.map((entry) => fileFingerprint(entry.file)));
      const additions = uploaded
        .filter((file) => !existingKeys.has(fileFingerprint(file)))
        .map((file) => ({ file, restaurantId: null as number | null }));
      return [...prev, ...additions].slice(0, MAX_SOA_FILES);
    });
  };

  const handleFileRemove = (removed: File) => {
    setFileEntries((prev) => prev.filter((entry) => fileFingerprint(entry.file) !== fileFingerprint(removed)));
  };

  const handleFileRemoveAll = () => setFileEntries([]);

  const handleFileRestaurantChange = (file: File, restaurantId: number | null) => {
    setFileEntries((prev) =>
      prev.map((entry) => (fileFingerprint(entry.file) === fileFingerprint(file) ? { ...entry, restaurantId } : entry))
    );
  };

  const handleDeleteDocument = (documentId: number) => {
    if (!activeSupplierId) return;
    setDeletingDocumentId(documentId);
    deleteDocument({ month: activeMonth, supplierId: activeSupplierId, documentId });
  };

  const handleSave = () => {
    if (!activeSupplierId) return;

    const entries: SoaEntryInput[] = [];
    for (const r of restaurants) {
      const rawDebit = entryValues[r.restaurantId]?.soaGmv ?? '';
      const rawCredit = entryValues[r.restaurantId]?.soaCredit ?? '';
      const hasDebit = rawDebit.trim() !== '';
      const hasCredit = rawCredit.trim() !== '';
      if (!hasDebit && !hasCredit) continue;

      if (!hasDebit) {
        setEntryError(`Enter a Debit value for ${r.restaurantName} before its Credit can be saved.`);
        return;
      }

      const soaGmv = Number(rawDebit);
      if (Number.isNaN(soaGmv) || soaGmv < 0) {
        setEntryError('Each Debit value must be a number, 0 or more.');
        return;
      }

      let soaCredit: number | undefined;
      if (hasCredit) {
        soaCredit = Number(rawCredit);
        if (Number.isNaN(soaCredit) || soaCredit < 0) {
          setEntryError('Each Credit value must be a number, 0 or more.');
          return;
        }
      }

      entries.push({ restaurantId: r.restaurantId, soaGmv, ...(soaCredit !== undefined ? { soaCredit } : {}) });
    }

    if (entries.length === 0) {
      setEntryError('Enter at least one Debit value.');
      return;
    }

    setEntryError(null);
    saveSoa({
      month: activeMonth,
      supplierId: activeSupplierId,
      entries,
      files: fileEntries.map((entry) => entry.file),
      fileRestaurants: fileEntries.map((entry) => entry.restaurantId),
    });
  };

  const canSave = !!activeSupplierId && !isSaving && !isClosed && restaurants.length > 0;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOverlayClick={false}
      size={{ base: 'full', md: '2xl' }}
      scrollBehavior='inside'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Add SOA — Statement of Account</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Stack spacing='md'>
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap='sm'>
              <GridItem>
                <Controller
                  control={control}
                  name='supplier'
                  render={({ field: { onChange, onBlur, value, name } }) => (
                    <FormControl isRequired>
                      <FormLabel>Supplier</FormLabel>
                      <ChakraAsyncSelect
                        loadOptions={loadSupplierOptions}
                        defaultOptions
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        name={name}
                        placeholder='Search suppliers...'
                        isClearable
                      />
                    </FormControl>
                  )}
                />
              </GridItem>

              <GridItem>
                <Controller
                  control={control}
                  name='month'
                  render={({ field: { onChange, onBlur, value, name } }) => (
                    <FormControl isRequired>
                      <FormLabel>Month</FormLabel>
                      <ChakraSelect
                        options={monthOptions}
                        onChange={onChange}
                        onBlur={onBlur}
                        value={value}
                        name={name}
                      />
                    </FormControl>
                  )}
                />
              </GridItem>
            </Grid>

            {pendingTermsCount > 0 && (
              <Alert status='warning' borderRadius='md'>
                <AlertIcon />
                <Text fontSize='sm' color='orange.700'>
                  {pendingTermsCount} {pendingTermsCount === 1 ? 'order has' : 'orders have'} no commission terms yet —
                  revenue excludes {pendingTermsCount === 1 ? 'it' : 'them'} until set on the{' '}
                  <Link
                    as={RouterLink}
                    to={`/app/gpo-orders/list?supplierId=${activeSupplierId}&dateFrom=${pendingTermsMonthStart}&dateTo=${pendingTermsMonthEnd}`}
                    onClick={handleClose}
                    color='orange.700'
                    textDecoration='underline'>
                    GPO Orders page
                  </Link>
                  .
                </Text>
              </Alert>
            )}

            {hasNoOrders && (
              <Alert status='warning' borderRadius='md'>
                <AlertIcon />
                No Kaso GPO orders for this supplier in {formatMonthLabel(activeMonth)} — there are no restaurant rows
                to reconcile yet.
              </Alert>
            )}

            {isClosed && (
              <Box bg='orange.50' border='1px solid' borderColor='orange.200' borderRadius='md' p='sm'>
                <Text fontSize='sm' color='orange.700'>
                  {selectedSupplier?.label} is closed for {formatMonthLabel(activeMonth)}. Reopen it from the table
                  before entering a new SOA.
                </Text>
              </Box>
            )}

            {!hasNoOrders && (
              <FormControl isInvalid={!!entryError}>
                <FormLabel>SOA per restaurant (AED)</FormLabel>

                {!activeSupplierId ? (
                  <Text fontSize='sm' color='gray.500'>
                    Select a supplier to enter SOA Debit/Credit per restaurant.
                  </Text>
                ) : isDetailLoading ? (
                  <Stack spacing='xs'>
                    <Skeleton height='32px' />
                    <Skeleton height='32px' />
                    <Skeleton height='32px' />
                  </Stack>
                ) : restaurants.length === 0 ? (
                  <Text fontSize='sm' color='gray.500'>
                    No restaurants found for this supplier/month.
                  </Text>
                ) : (
                  <Stack spacing='xs' maxH='280px' overflowY='auto'>
                    {/* On base the restaurant name takes the full first line and Debit/Credit share a
                        second — the same `1fr 1fr` template + full-span name cell in the header keeps
                        the label columns aligned with the inputs on both breakpoints. */}
                    <Grid templateColumns={{ base: '1fr 1fr', md: '2fr 1fr 1fr' }} gap='sm'>
                      <GridItem gridColumn={{ base: '1 / -1', md: 'auto' }} display={{ base: 'none', md: 'block' }} />
                      <GridItem>
                        <Text fontSize='xs' color='gray.500' textAlign='right'>
                          Debit — orders submitted
                        </Text>
                      </GridItem>
                      <GridItem>
                        <Text fontSize='xs' color='gray.500' textAlign='right'>
                          Credit — orders paid
                        </Text>
                      </GridItem>
                    </Grid>

                    {restaurants.map((r) => (
                      <Grid
                        key={r.restaurantId}
                        templateColumns={{ base: '1fr 1fr', md: '2fr 1fr 1fr' }}
                        gap='sm'
                        alignItems='center'>
                        <GridItem gridColumn={{ base: '1 / -1', md: 'auto' }}>
                          <Text fontSize='sm'>{r.restaurantName}</Text>
                        </GridItem>
                        <GridItem>
                          <Input
                            size='sm'
                            type='number'
                            min={0}
                            step='0.01'
                            textAlign='right'
                            isDisabled={isClosed}
                            value={entryValues[r.restaurantId]?.soaGmv ?? ''}
                            onChange={(e) => {
                              setEntryError(null);
                              setEntryValues((prev) => ({
                                ...prev,
                                [r.restaurantId]: {
                                  soaGmv: e.target.value,
                                  soaCredit: prev[r.restaurantId]?.soaCredit ?? '',
                                },
                              }));
                            }}
                          />
                        </GridItem>
                        <GridItem>
                          <Input
                            size='sm'
                            type='number'
                            min={0}
                            step='0.01'
                            textAlign='right'
                            isDisabled={isClosed}
                            value={entryValues[r.restaurantId]?.soaCredit ?? ''}
                            onChange={(e) => {
                              setEntryError(null);
                              setEntryValues((prev) => ({
                                ...prev,
                                [r.restaurantId]: {
                                  soaGmv: prev[r.restaurantId]?.soaGmv ?? '',
                                  soaCredit: e.target.value,
                                },
                              }));
                            }}
                          />
                        </GridItem>
                      </Grid>
                    ))}
                  </Stack>
                )}

                <FormErrorMessage>{entryError}</FormErrorMessage>
              </FormControl>
            )}

            <FormControl>
              <FormLabel>Statement documents (optional)</FormLabel>
              <FileUploader
                style='style_01'
                message='Drop supplier SOA files here or browse — PDF, XLSX or image (up to 10)'
                acceptedTypes={STATEMENT_ACCEPT_TYPES}
                multiple
                maxFiles={MAX_SOA_FILES}
                // Explicit — FileUploader's own default is 5MB, which would silently reject the
                // 5–10MB phone photos the BE statement-upload contract now allows.
                maxFileSize={10 * 1024 * 1024}
                onRemove={handleFileRemove}
                onRemoveAll={handleFileRemoveAll}
                onUpload={handleFileUpload}
              />

              {!!fileEntries.length && (
                <Stack spacing='xs' mt='sm'>
                  {fileEntries.map((entry) => (
                    <HStack
                      key={fileFingerprint(entry.file)}
                      spacing='sm'
                      flexWrap={{ base: 'wrap', md: 'nowrap' }}
                      rowGap='xs'>
                      <Text fontSize='sm' isTruncated flex='1'>
                        {entry.file.name}
                      </Text>
                      <Box minW={{ base: 'full', md: '160px' }}>
                        <ChakraSelect
                          size='sm'
                          options={fileRestaurantOptions}
                          value={
                            fileRestaurantOptions.find((o) => o.value === entry.restaurantId) ??
                            fileRestaurantOptions[0]
                          }
                          onChange={(opt: any) => handleFileRestaurantChange(entry.file, opt?.value ?? null)}
                        />
                      </Box>
                      <IconButtonWithTooltip
                        label='Remove'
                        aria-label='remove-soa-file'
                        size='sm'
                        variant='ghost'
                        Icon={<Icon as={FiTrash2} />}
                        onClick={() => handleFileRemove(entry.file)}
                      />
                    </HStack>
                  ))}
                </Stack>
              )}
            </FormControl>

            {!!documents.length && (
              <FormControl>
                <FormLabel>Uploaded statements</FormLabel>
                <Stack spacing='xs'>
                  {documents.map((doc) => (
                    <HStack key={doc.id} justify='space-between' spacing='sm'>
                      <HStack spacing='sm' overflow='hidden'>
                        <Link
                          href={doc.url}
                          target='_blank'
                          rel='noopener noreferrer'
                          color='red.500'
                          fontSize='sm'
                          isTruncated>
                          {doc.fileName}
                        </Link>
                        <Text fontSize='xs' color='gray.500' whiteSpace='nowrap'>
                          {doc.restaurantName ?? 'Supplier-wide'}
                        </Text>
                      </HStack>
                      {!isClosed && (
                        <IconButtonWithTooltip
                          label='Delete'
                          aria-label='delete-soa-document'
                          size='sm'
                          variant='ghost'
                          colorScheme='red'
                          isLoading={isDeletingDocument && deletingDocumentId === doc.id}
                          Icon={<Icon as={FiTrash2} />}
                          onClick={() => handleDeleteDocument(doc.id)}
                        />
                      )}
                    </HStack>
                  ))}
                </Stack>
              </FormControl>
            )}
          </Stack>
        </ModalBody>

        <ModalFooter>
          <HStack spacing='sm'>
            <Button variant='outline' onClick={handleClose} isDisabled={isSaving}>
              Cancel
            </Button>
            <Button colorScheme='red' onClick={handleSave} isLoading={isSaving} isDisabled={!canSave}>
              Save SOA
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
